import { useCallback, useEffect, useRef } from "react";

// Third Party
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// APIs
import { logout } from "../services/https/auth";

// Redux
import { RootState } from "../store/store";
import { setMyDetails } from "../store/usersSlice";

// Helpers
import { getLocalStorage, clearLocalStorage } from "../helpers/utils";

// Log the user out after this much inactivity (frontend-only enforcement).
const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes

// User interactions that count as "activity" and reset the idle timer.
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "click",
];

/**
 * Logs the user out after 15 minutes of inactivity.
 *
 * The timer only runs while the user is authenticated and resets on any user
 * interaction. When it fires, it calls the logout API and then clears the local
 * session (storage + redux) and redirects to the login page.
 */
export const useIdleTimer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loginDetails } = useSelector((state: RootState) => state.user);

  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const isLoggingOutRef = useRef(false);

  const isAuthenticated = Boolean(
    getLocalStorage("token") || loginDetails?.access_token,
  );

  const handleIdleLogout = useCallback(async () => {
    // Guard against the timer firing more than once.
    if (isLoggingOutRef.current || !getLocalStorage("token")) return;
    isLoggingOutRef.current = true;

    try {
      // Call the logout API before clearing the token so it can be sent.
      await logout();
    } finally {
      clearLocalStorage();
      dispatch(setMyDetails({}));
      navigate("/login");
      toast.info(
        "You have been logged out due to inactivity.",
      );
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    // Don't run the idle timer on public/unauthenticated pages.
    if (!isAuthenticated) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    isLoggingOutRef.current = false;

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(handleIdleLogout, IDLE_TIMEOUT);
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, resetTimer),
    );

    // Arm the timer immediately so an idle session still logs out.
    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, resetTimer),
      );
    };
  }, [isAuthenticated, handleIdleLogout]);
};
